/**
 * "Verified design libraries" — the browsable surface over the admitted
 * design-source libraries (FINDING-006's missing half: the DS ↔ OpenPencil
 * bridge existed, the admitted-library list did not).
 *
 * Every row is a projection of in-repo receipts via the committed registry
 * (see read-verified-design-libraries.ts) — this component holds NO library
 * roster of its own and renders NOTHING when the registry is unreadable
 * except the failure itself. Two truthfulness rules it must keep:
 *
 *  1. Admission ≠ derivation rights (library-harness discipline). A library
 *     being listed — even fully reuse-admitted — never implies generation
 *     eligibility; the generation boundary is stated on every row from the
 *     vault's own `generationEligible` fact.
 *  2. Stage wording comes from evidence, not aspiration: a source blocked at
 *     a gate says so, with the gate named; a designated-only source says the
 *     gates have not run.
 *
 * Tone follows the screen rule (see AxisResolutionList's module doc):
 * attention-red is reserved for a genuine read failure — the fail-closed
 * `unreadable` card. "Blocked at a gate" is an ordinary, expected pipeline
 * state and renders in muted foreground like every other fact.
 *
 * COPY. Every sentence comes from `./verified-libraries-copy.ts` (co-located
 * table + accessor, the `appliance-admin-page-copy.ts` pattern) — this panel
 * shipped entirely in English on a `ko` surface until 2026-08-18. Registry
 * DATA still passes through verbatim: `sourceId`, `snapshot.status`, the
 * sha256 hex, `receiptId`, the SPDX id and its status, and `blockedAtGate`
 * are the vault's own strings and are matched against in-repo receipts, so
 * they are never translated. See that module's header for the per-term call.
 */
import { useStudioLocale } from '@/i18n/use-studio-locale'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'
import { capabilityGrantSchema } from './manifest-schema'
import type { CapabilityGrant, VerifiedDesignLibrary } from './manifest-schema'
import type { VerifiedDesignLibrariesRead } from './read-verified-design-libraries'
import {
  getVerifiedLibrariesCopy,
  type VerifiedLibrariesCopy,
} from './verified-libraries-copy'

/**
 * Axis order on the scope line, read off the SCHEMA rather than off a copy
 * table — the previous inline `CAPABILITY_LABELS` object was both the label
 * source and the order source, so moving the labels into a locale table would
 * have made display order depend on whichever locale happened to be active.
 * `capabilityGrantSchema.shape` keeps its declaration order, so a seventh
 * grant axis appears here automatically (and is a compile error in
 * `verified-libraries-copy.ts` until every locale names it).
 */
const CAPABILITY_ORDER = Object.keys(
  capabilityGrantSchema.shape,
) as (keyof CapabilityGrant)[]

function capabilityNames(
  grant: CapabilityGrant,
  admitted: boolean,
  copy: VerifiedLibrariesCopy,
): string {
  return CAPABILITY_ORDER.filter((key) => grant[key] === admitted)
    .map((key) => copy.capabilityLabels[key])
    .join(copy.capabilitySeparator)
}

/** Mechanical display segment: the sourceId minus its vendor prefix — never a
 * hand-maintained pretty name, so a new vault entry needs no code change.
 *
 * Exported for the i18n residue gate, which must treat what this RENDERS as
 * registry data. Deriving it there from the component rather than re-typing
 * `sourceId.split('.')[1]` is the point: if this rule changes, the gate
 * follows it instead of silently flagging the new rendering as English. */
export function displaySegment(sourceId: string): string {
  const dot = sourceId.indexOf('.')
  return dot > 0 ? sourceId.slice(dot + 1) : sourceId
}

export function VerifiedLibrariesSection({ read }: { read: VerifiedDesignLibrariesRead }) {
  const { locale } = useStudioLocale()
  const copy = getVerifiedLibrariesCopy(locale)

  if (read.status === 'unreadable') {
    return (
      <section
        data-testid="verified-libraries-unreadable"
        className="rounded-lg border border-destructive/30 bg-card px-4 py-3 text-xs text-destructive"
      >
        {copy.unreadable(read.reason)}
      </section>
    )
  }

  return (
    <section
      data-testid="verified-libraries-section"
      className="rounded-lg border border-border bg-card px-4 py-3"
    >
      <header>
        <h2 className="text-sm font-semibold text-foreground">{copy.sectionTitle}</h2>
        <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
          {copy.sectionNote}
        </p>
      </header>
      {read.manifest.libraries.length === 0 ? (
        <p
          data-testid="verified-libraries-empty"
          className="mt-3 text-[13px] leading-5 text-muted-foreground"
        >
          {copy.empty}
        </p>
      ) : (
        <div className="mt-3 flex flex-col gap-3">
          {read.manifest.libraries.map((library) => (
            <LibraryRow
              key={library.sourceId}
              library={library}
              copy={copy}
              locale={locale}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function LibraryRow({
  library,
  copy,
  locale,
}: {
  library: VerifiedDesignLibrary
  copy: VerifiedLibrariesCopy
  /** Number grouping for the byte count — was hardcoded `'en-US'` on a screen
   * that now renders in five locales. All five group in threes, so this is not
   * a visible change today; it stops being a hardcoded locale, which is. */
  locale: LocaleCode
}) {
  const { snapshot } = library
  return (
    <div
      data-testid="verified-library-row"
      className="rounded-md border border-border bg-background px-3 py-3"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-sm font-semibold text-foreground">
          {displaySegment(library.sourceId)}
        </span>
        <span className="font-mono text-[11px] text-muted-foreground">{library.sourceId}</span>
        {library.communityUrl ? (
          <a
            href={library.communityUrl}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
          >
            {copy.communityLink}
          </a>
        ) : null}
      </div>

      <p className="mt-1 text-xs leading-5 text-muted-foreground">
        {copy.snapshotLabel} {snapshot.status}
        {snapshot.sha256 ? (
          <>
            {copy.scopeSeparator}
            {copy.sha256(snapshot.sha256.slice(0, 12))}
          </>
        ) : null}
        {snapshot.byteLength !== undefined ? (
          <>
            {copy.scopeSeparator}
            {copy.bytes(snapshot.byteLength.toLocaleString(locale))}
          </>
        ) : null}
      </p>

      <AdmissionLine library={library} copy={copy} />
      <RightsScopeLine library={library} copy={copy} />

      <p className="mt-1 text-xs leading-5 text-muted-foreground">
        {library.generationEligible
          ? copy.generationEligible
          : copy.generationNotAdmitted}
      </p>
    </div>
  )
}

function AdmissionLine({
  library,
  copy,
}: {
  library: VerifiedDesignLibrary
  copy: VerifiedLibrariesCopy
}) {
  const { admission } = library
  switch (admission.stage) {
    case 'library-reuse-admitted':
      return (
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          {copy.reuseAdmitted}{' '}
          <span className="font-mono text-[11px]">
            {admission.reuseReceipts.map((receipt) => receipt.receiptId).join(', ')}
          </span>
          {admission.agreedLicense
            ? `${copy.scopeSeparator}${copy.license(
                admission.agreedLicense.status,
                admission.agreedLicense.spdx,
              )}`
            : null}
        </p>
      )
    case 'admission-blocked':
      return (
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          {copy.admissionBlocked}
          {admission.blockedAtGate ? (
            <>
              {' — '}
              {copy.nextRequiredGate}{' '}
              <span className="font-mono text-[11px]">{admission.blockedAtGate}</span>
            </>
          ) : null}
        </p>
      )
    case 'designated':
      return (
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          {copy.designated}
        </p>
      )
    case 'no-admission-evidence':
      return (
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          {copy.noAdmissionEvidence}
        </p>
      )
  }
}

function RightsScopeLine({
  library,
  copy,
}: {
  library: VerifiedDesignLibrary
  copy: VerifiedLibrariesCopy
}) {
  const { admission } = library
  if (admission.stage !== 'library-reuse-admitted') return null
  if (!admission.agreedGrant) {
    return (
      <p className="mt-1 text-xs leading-5 text-muted-foreground">
        {copy.grantVariesByReceipt}
      </p>
    )
  }
  const admitted = capabilityNames(admission.agreedGrant, true, copy)
  const notAdmitted = capabilityNames(admission.agreedGrant, false, copy)
  return (
    <p className="mt-1 text-xs leading-5 text-muted-foreground">
      {admitted ? copy.admittedList(admitted) : copy.admittedNone}
      {notAdmitted
        ? `${copy.scopeSeparator}${copy.notAdmittedList(notAdmitted)}`
        : null}
    </p>
  )
}
